import { useEffect, useState } from "react";
import { userRoles } from "@/lib/constants";
import { Bell, BellSlash, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { pushSupported, pushPermission, enablePush, disablePush, sendTestPush, currentSubscription } from "@/lib/push";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const AUTO_PROMPTED_KEY = "dh_push_prompted_v1";
const DECLINED_KEY = "dh_push_declined_v1";

/**
 * Toolbar button rendered in the mobile top-bar and desktop sidebar.
 * Shows the current push state and lets the user opt in/out.
 * On first login (once per browser) it politely opens itself so the
 * user doesn't have to hunt for it.
 */
export default function NotificationToggle({ compact = false }) {
  const { user } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!pushSupported()) return;
      const sub = await currentSubscription();
      if (!cancelled) setEnabled(!!sub && pushPermission() === "granted");
    })();
    return () => { cancelled = true; };
  }, []);

  // First-time gentle prompt for stage roles (they benefit the most)
  useEffect(() => {
    if (!pushSupported()) return;
    if (pushPermission() !== "default") return;
    if (localStorage.getItem(AUTO_PROMPTED_KEY)) return;
    if (localStorage.getItem(DECLINED_KEY)) return;
    const stageRole = userRoles(user).some(r => ["picker","scanner","packer","accounts","admin"].includes(r));
    if (!stageRole) return;
    const t = setTimeout(() => setOpen(true), 3000);
    return () => clearTimeout(t);
  }, [user?.roles?.join(",")]);

  const turnOn = async () => {
    setBusy(true);
    const res = await enablePush();
    setBusy(false);
    if (res.ok) {
      setEnabled(true); setOpen(false);
      localStorage.setItem(AUTO_PROMPTED_KEY, "1");
      localStorage.removeItem(DECLINED_KEY);
      toast.success("Notifications enabled");
    } else if (res.reason === "denied") {
      toast.error("Blocked in the browser. Enable notifications for this site in Settings.");
      localStorage.setItem(DECLINED_KEY, "1");
    } else if (res.reason === "unsupported") {
      toast.error("Your browser does not support push notifications.");
    } else {
      toast.error("Could not enable notifications");
    }
  };

  const turnOff = async () => {
    setBusy(true); await disablePush(); setBusy(false);
    setEnabled(false); localStorage.setItem(DECLINED_KEY, "1");
    toast.success("Notifications disabled");
  };

  const testPing = async () => {
    try {
      const { data } = await sendTestPush();
      toast.success(`Sent ${data.delivered}/${data.subscriptions} test notification${data.subscriptions === 1 ? "" : "s"}`);
    } catch { toast.error("Failed to send test"); }
  };

  const dismissPrompt = () => {
    setOpen(false);
    localStorage.setItem(AUTO_PROMPTED_KEY, "1");
    localStorage.setItem(DECLINED_KEY, "1");
  };

  if (!pushSupported()) return null;

  return (
    <>
      <button
        data-testid="notif-toggle"
        onClick={() => setOpen(true)}
        className={`${compact ? "h-9 w-9" : "h-10 w-10"} relative grid place-items-center rounded-sm hover:bg-orange-100 text-orange-950`}
        title={enabled ? "Notifications on" : "Notifications off"}
      >
        {enabled ? <Bell size={compact ? 16 : 18} weight="fill" /> : <BellSlash size={compact ? 16 : 18} />}
        {enabled && (
          <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-emerald-500 ring-2 ring-orange-50"/>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/50 p-4" onClick={dismissPrompt}>
          <div
            data-testid="notif-modal"
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-sm border border-slate-200 shadow-2xl w-full max-w-md p-5"
            style={{ paddingBottom: "calc(1.25rem + env(safe-area-inset-bottom, 0px))" }}
          >
            <div className="flex items-start gap-3">
              <div className="h-11 w-11 rounded-sm bg-orange-600 text-white grid place-items-center shrink-0">
                <Bell size={22} weight="bold"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-base font-semibold text-slate-900">Get pinged when the pipeline moves</div>
                <div className="text-xs text-slate-600 mt-1 leading-relaxed">
                  Turn on notifications and DispatchHub will alert you the moment an order enters your stage —
                  even when the app is closed. You can turn this off anytime.
                </div>

                <div className="mt-3 text-xs">
                  <div className="flex items-center gap-2 text-slate-700">
                    {enabled ? <CheckCircle size={14} weight="fill" className="text-emerald-600"/> : <WarningCircle size={14} weight="fill" className="text-amber-600"/>}
                    <span>Status: <b>{enabled ? "Enabled on this device" : "Disabled"}</b></span>
                  </div>
                  {pushPermission() === "denied" && (
                    <div className="mt-2 text-rose-700 bg-rose-50 border border-rose-200 rounded-sm px-2 py-1">
                      Notifications are blocked in the browser. Open the site settings (padlock icon in address bar) and allow notifications.
                    </div>
                  )}
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {!enabled ? (
                    <button data-testid="notif-enable" disabled={busy} onClick={turnOn}
                            className="text-sm font-semibold bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 rounded-sm">
                      {busy ? "Enabling…" : "Enable notifications"}
                    </button>
                  ) : (
                    <>
                      <button data-testid="notif-test"    onClick={testPing}   className="text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white px-3 py-2 rounded-sm">Send test</button>
                      <button data-testid="notif-disable" onClick={turnOff} disabled={busy} className="text-sm font-semibold bg-white hover:bg-rose-50 text-rose-700 border border-rose-300 px-3 py-2 rounded-sm">Disable</button>
                    </>
                  )}
                  <button data-testid="notif-close" onClick={dismissPrompt} className="text-sm font-semibold text-slate-600 hover:bg-slate-100 px-3 py-2 rounded-sm ml-auto">Close</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
